// Frontend-only mock data for the Ops → Datasets page (Prompt Eval System, M1).
// There is no backend yet; these fixtures drive the list → detail views and
// support non-persisting local-state interactions, mirroring how Ops → Prompts
// shipped (see src/app/components/ops-prompts/mockData.ts).
//
// The local types below are UI-shaped prototypes. When the backend lands they map
// to persisted GraphQL models (Dataset / DatasetItem) and the golden extraction to
// the real Call extraction shape (CallPerson / PersonSummary). See
// docs/plans/2026-09-11-prompt-eval-system-plan.md §4 and §8.

/** One person in a golden (human-corrected) extraction: the structured fields of the
 *  pipeline's final output person (FinalPerson). Unset optional fields mean "empty".
 *  Call-level free text (summary/actionItems) is not scored, so goldens omit it. */
export type GoldenPerson = {
  name: string
  isCaller: boolean
  patientType: string
  /** e.g. `son`; empty for the caller. */
  relationship?: string
  /** 10 digits, as the pipeline saves it. */
  phone?: string
  email?: string
  /** YYYY-MM-DD. */
  birthDate?: string
  appointmentRequested: boolean
  appointmentScheduled: boolean
  /** YYYY-MM-DD when scheduled. */
  appointmentDate?: string
  /** h:mm a when scheduled. */
  appointmentTime?: string
  /** One of the pipeline's treatment options. */
  treatment?: string
  /** Free text; scored on whether it is present. */
  serviceRequested?: string
  /** Free text; scored on whether it is present. */
  reasonForNotScheduling?: string
  outcomes: string[]
  insuranceProvider?: string
  /** How they heard about the practice; the pipeline falls back to the call's channel. */
  referral?: string
}

/** The human-labeled expected extraction for a call — the target a candidate prompt
 *  is scored against. */
export type GoldenExtraction = {
  answerer?: string
  channel?: string
  callType?: string
  people: GoldenPerson[]
  /** Free text a pipeline produces; shown for review, never scored. Goldens omit it. */
  summary?: string
  actionItems?: string
}

/** What produced the ORIGINAL AI output for this call (for auditability). */
export type ItemProvenance = {
  model: string
  temperature: number
  /** Stage → prompt version that produced the original AI extraction. */
  promptVersions: Record<string, number>
}

/** A single labeled call in the dataset universe. Belongs to any dataset whose
 *  defining tag is in `tags` (datasets are tag queries over items — many-to-many). */
export type DatasetItem = {
  id: string
  callId: string
  /** ISO timestamp of the call. */
  callDate: string
  callerName: string
  /** Snapshot of the transcript so a changed call can't silently move the target. */
  transcriptSnapshot: string
  golden: GoldenExtraction
  tags: string[]
  labeledBy: string
  /** ISO timestamp. */
  labeledAt: string
  provenance: ItemProvenance
}

/** A named collection of items, defined by a membership `tag`. */
export type Dataset = {
  id: string
  name: string
  description: string
  /** Items whose `tags` include this belong to the dataset. */
  tag: string
  createdBy: string
  /** ISO timestamp. */
  createdAt: string
}

const TRANSCRIPT_INSURANCE = `[00:00] Front Desk: Thanks for calling Smile Dental, this is Maria.
[00:04] Caller: Hi, do you take Medi-Cal for my son?
[00:08] Front Desk: I'm sorry, we don't accept Medi-Cal at this location.
[00:12] Caller: Oh okay. Do you know anywhere that does?
[00:16] Front Desk: You could try the community clinic on 5th. Sorry about that.
[00:20] Caller: Alright, thank you.`

const TRANSCRIPT_RESCHEDULE = `[00:00] Front Desk: Smile Dental, this is Jen.
[00:03] Caller: Hi, this is David Okafor. I have a cleaning Friday at 2 but I need to move it.
[00:09] Front Desk: No problem David. How about the following Tuesday, the 16th, at 10am?
[00:14] Caller: That works great.
[00:16] Front Desk: You're all set for the 16th at 10.`

const TRANSCRIPT_NEW_PATIENT = `[00:00] Front Desk: Thank you for calling, this is Priya.
[00:03] Caller: Hi, I'd like to book a new patient exam. My name is Sam Reyes.
[00:08] Front Desk: Wonderful. We have an opening next Wednesday the 17th at 9:30am.
[00:13] Caller: Perfect, I'll take it.
[00:15] Front Desk: Great, see you the 17th at 9:30.`

const TRANSCRIPT_FAMILY = `[00:00] Front Desk: Bright Smiles, this is Carla.
[00:03] Caller: Hi, I'm Jordan Rodriguez. I want to schedule cleanings for my two kids, Abigail and Mateo.
[00:10] Front Desk: Sure! Both on the same day? We have the 12th open in the afternoon.
[00:15] Caller: Yes please, whatever times work.
[00:18] Front Desk: I've got Abigail at 3 and Mateo at 3:30 on the 12th.`

const LABELED_ITEMS: DatasetItem[] = [
  {
    id: `item_insurance_1`,
    callId: `call_9001`,
    callDate: `2026-08-14T16:22:00Z`,
    callerName: `Unknown Caller`,
    transcriptSnapshot: TRANSCRIPT_INSURANCE,
    tags: [`call_processing_golden`, `insurance_rejection`],
    labeledBy: `Dylan Ryan`,
    labeledAt: `2026-08-20T14:10:00Z`,
    provenance: { model: `gpt-5-mini`, temperature: 0, promptVersions: { people: 17, outcome: 13 } },
    golden: {
      answerer: `Maria`,
      channel: undefined,
      callType: `insurance_inquiry`,
      people: [
        {
          name: `unknown`,
          isCaller: true,
          patientType: `Non-Patient`,
          appointmentRequested: false,
          appointmentScheduled: false,
          outcomes: [`Insurance: State / HMO`, `Other`],
        },
        {
          name: `unknown`,
          isCaller: false,
          patientType: `New Patient`,
          appointmentRequested: true,
          appointmentScheduled: false,
          outcomes: [`Insurance: State / HMO`, `Appointment Not Scheduled`],
        },
      ],
    },
  },
  {
    id: `item_reschedule_1`,
    callId: `call_9002`,
    callDate: `2026-08-15T15:02:00Z`,
    callerName: `David Okafor`,
    transcriptSnapshot: TRANSCRIPT_RESCHEDULE,
    tags: [`call_processing_golden`, `appointment_golden`],
    labeledBy: `Sara Chen`,
    labeledAt: `2026-08-21T09:30:00Z`,
    provenance: { model: `gpt-5-mini`, temperature: 0, promptVersions: { appointment: 12, outcome: 13 } },
    golden: {
      answerer: `Jen`,
      callType: `reschedule`,
      people: [
        {
          name: `David Okafor`,
          isCaller: true,
          patientType: `Current Patient`,
          appointmentRequested: true,
          appointmentScheduled: true,
          appointmentDate: `2026-08-16`,
          appointmentTime: `10:00 am`,
          outcomes: [`Appointment Rescheduled`],
        },
      ],
    },
  },
  {
    id: `item_newpatient_1`,
    callId: `call_9003`,
    callDate: `2026-08-16T17:41:00Z`,
    callerName: `Sam Reyes`,
    transcriptSnapshot: TRANSCRIPT_NEW_PATIENT,
    tags: [`call_processing_golden`],
    labeledBy: `Dylan Ryan`,
    labeledAt: `2026-08-21T11:05:00Z`,
    provenance: { model: `gpt-5-mini`, temperature: 0, promptVersions: { people: 17, appointment: 12 } },
    golden: {
      answerer: `Priya`,
      callType: `new_appointment`,
      people: [
        {
          name: `Sam Reyes`,
          isCaller: true,
          patientType: `New Patient`,
          appointmentRequested: true,
          appointmentScheduled: true,
          appointmentDate: `2026-08-17`,
          appointmentTime: `9:30 am`,
          outcomes: [`Appointment Scheduled`],
        },
      ],
    },
  },
  {
    id: `item_family_1`,
    callId: `call_9004`,
    callDate: `2026-08-17T18:12:00Z`,
    callerName: `Jordan Rodriguez`,
    transcriptSnapshot: TRANSCRIPT_FAMILY,
    tags: [`call_processing_golden`, `difficult_names`],
    labeledBy: `Marcus Lee`,
    labeledAt: `2026-08-22T13:20:00Z`,
    provenance: { model: `gpt-4o`, temperature: 0, promptVersions: { people: 17, appointment: 12 } },
    golden: {
      answerer: `Carla`,
      callType: `new_appointment`,
      people: [
        {
          name: `Jordan Rodriguez`,
          isCaller: true,
          patientType: `Non-Patient`,
          appointmentRequested: false,
          appointmentScheduled: false,
          outcomes: [`Other`],
        },
        {
          name: `Abigail Rodriguez`,
          isCaller: false,
          patientType: `New Patient`,
          appointmentRequested: true,
          appointmentScheduled: true,
          appointmentDate: `2026-08-12`,
          appointmentTime: `3:00 pm`,
          outcomes: [`Appointment Scheduled`],
        },
        {
          name: `Mateo Rodriguez`,
          isCaller: false,
          patientType: `New Patient`,
          appointmentRequested: true,
          appointmentScheduled: true,
          appointmentDate: `2026-08-12`,
          appointmentTime: `3:30 pm`,
          outcomes: [`Appointment Scheduled`],
        },
      ],
    },
  },
]

// Routine single-person calls that fill out the golden set, so seed runs have enough
// calls for a verdict (the stats need at least six).
type RoutineCall = {
  kind: `new` | `reschedule` | `cancel`
  caller: string
  answerer: string
  practice: string
  /** YYYY-MM-DD of the call; the appointment, when there is one, is `apptDate`. */
  callDate: string
  apptDate?: string
  spokenDate?: string
  time?: string
}

const ROUTINE_CALLS: RoutineCall[] = [
  {
    kind: `new`,
    caller: `Nora Whitfield`,
    answerer: `Priya`,
    practice: `Smile Dental`,
    callDate: `2026-08-03`,
    apptDate: `2026-08-06`,
    spokenDate: `Thursday the 6th`,
    time: `11:00 am`,
  },
  {
    kind: `reschedule`,
    caller: `Luis Ortega`,
    answerer: `Jen`,
    practice: `Smile Dental`,
    callDate: `2026-08-03`,
    apptDate: `2026-08-11`,
    spokenDate: `Tuesday the 11th`,
    time: `2:30 pm`,
  },
  { kind: `cancel`, caller: `Grace Park`, answerer: `Maria`, practice: `Smile Dental`, callDate: `2026-08-04` },
  {
    kind: `new`,
    caller: `Ethan Brooks`,
    answerer: `Carla`,
    practice: `Bright Smiles`,
    callDate: `2026-08-05`,
    apptDate: `2026-08-10`,
    spokenDate: `Monday the 10th`,
    time: `8:00 am`,
  },
  {
    kind: `reschedule`,
    caller: `Amara Nwosu`,
    answerer: `Carla`,
    practice: `Bright Smiles`,
    callDate: `2026-08-06`,
    apptDate: `2026-08-13`,
    spokenDate: `Thursday the 13th`,
    time: `4:00 pm`,
  },
  {
    kind: `new`,
    caller: `Henry Castillo`,
    answerer: `Jen`,
    practice: `Smile Dental`,
    callDate: `2026-08-07`,
    apptDate: `2026-08-12`,
    spokenDate: `Wednesday the 12th`,
    time: `1:15 pm`,
  },
  { kind: `cancel`, caller: `Olivia Grant`, answerer: `Priya`, practice: `Smile Dental`, callDate: `2026-08-10` },
  {
    kind: `reschedule`,
    caller: `Kenji Tanaka`,
    answerer: `Maria`,
    practice: `Smile Dental`,
    callDate: `2026-08-10`,
    apptDate: `2026-08-18`,
    spokenDate: `Tuesday the 18th`,
    time: `10:30 am`,
  },
  {
    kind: `new`,
    caller: `Fatima Haddad`,
    answerer: `Carla`,
    practice: `Bright Smiles`,
    callDate: `2026-08-11`,
    apptDate: `2026-08-14`,
    spokenDate: `Friday the 14th`,
    time: `9:00 am`,
  },
  {
    kind: `new`,
    caller: `Caleb Morgan`,
    answerer: `Priya`,
    practice: `Smile Dental`,
    callDate: `2026-08-12`,
    apptDate: `2026-08-19`,
    spokenDate: `Wednesday the 19th`,
    time: `3:45 pm`,
  },
  {
    kind: `reschedule`,
    caller: `Sofia Marino`,
    answerer: `Jen`,
    practice: `Smile Dental`,
    callDate: `2026-08-13`,
    apptDate: `2026-08-20`,
    spokenDate: `Thursday the 20th`,
    time: `12:00 pm`,
  },
  { kind: `cancel`, caller: `Isaac Levy`, answerer: `Carla`, practice: `Bright Smiles`, callDate: `2026-08-13` },
]

function routineTranscript(call: RoutineCall): string {
  if (call.kind === `new`) {
    return `[00:00] Front Desk: Thank you for calling ${call.practice}, this is ${call.answerer}.
[00:03] Caller: Hi, I'd like to book a new patient exam. My name is ${call.caller}.
[00:08] Front Desk: Wonderful. We have an opening ${call.spokenDate} at ${call.time}.
[00:13] Caller: Perfect, I'll take it.
[00:15] Front Desk: Great, see you then.`
  }
  if (call.kind === `reschedule`) {
    return `[00:00] Front Desk: ${call.practice}, this is ${call.answerer}.
[00:03] Caller: Hi, this is ${call.caller}. I need to move my cleaning.
[00:07] Front Desk: No problem. How about ${call.spokenDate} at ${call.time}?
[00:11] Caller: That works.
[00:13] Front Desk: You're all set.`
  }
  return `[00:00] Front Desk: ${call.practice}, this is ${call.answerer}.
[00:03] Caller: Hi, it's ${call.caller}. I need to cancel my appointment next week. I'll call back to rebook.
[00:09] Front Desk: Okay, I've canceled it. Talk soon.`
}

const ROUTINE_GOLDEN = {
  new: { callType: `new_appointment`, patientType: `New Patient`, outcome: `Appointment Scheduled` },
  reschedule: { callType: `reschedule`, patientType: `Current Patient`, outcome: `Appointment Rescheduled` },
  cancel: { callType: `cancellation`, patientType: `Current Patient`, outcome: `Canceled Appointment` },
}

export const MOCK_ITEMS: DatasetItem[] = [
  ...LABELED_ITEMS,
  ...ROUTINE_CALLS.map((call, index): DatasetItem => {
    const golden = ROUTINE_GOLDEN[call.kind]
    const scheduled = call.kind !== `cancel`
    return {
      id: `item_routine_${index + 1}`,
      callId: `call_${9101 + index}`,
      callDate: `${call.callDate}T16:00:00Z`,
      callerName: call.caller,
      transcriptSnapshot: routineTranscript(call),
      tags: call.kind === `new` ? [`call_processing_golden`] : [`call_processing_golden`, `appointment_golden`],
      labeledBy: `Sara Chen`,
      labeledAt: `2026-08-19T10:00:00Z`,
      provenance: { model: `gpt-5-mini`, temperature: 0, promptVersions: { people: 16, appointment: 11, outcome: 12 } },
      golden: {
        answerer: call.answerer,
        callType: golden.callType,
        people: [
          {
            name: call.caller,
            isCaller: true,
            patientType: golden.patientType,
            appointmentRequested: scheduled,
            appointmentScheduled: scheduled,
            appointmentDate: call.apptDate,
            appointmentTime: call.time,
            outcomes: [golden.outcome],
          },
        ],
      },
    }
  }),
]

export const MOCK_DATASETS: Dataset[] = [
  {
    id: `ds_call_processing_golden`,
    name: `Call Processing Golden`,
    description: `Broad set of correctly-labeled call extractions — the primary regression set for the whole pipeline.`,
    tag: `call_processing_golden`,
    createdBy: `Dylan Ryan`,
    createdAt: `2026-08-20T14:00:00Z`,
  },
  {
    id: `ds_insurance_rejection`,
    name: `Insurance Rejection`,
    description: `Calls where the office does not accept the caller's insurance — targets the outcome + people stages.`,
    tag: `insurance_rejection`,
    createdBy: `Dylan Ryan`,
    createdAt: `2026-08-20T14:05:00Z`,
  },
  {
    id: `ds_difficult_names`,
    name: `Difficult Names`,
    description: `Unnamed minors and surname propagation — isolates the people stage.`,
    tag: `difficult_names`,
    createdBy: `Marcus Lee`,
    createdAt: `2026-08-22T13:00:00Z`,
  },
  {
    id: `ds_appointment_golden`,
    name: `Appointment Golden`,
    description: `Reschedules, cancellations, and date-inference edge cases — isolates the appointment stage.`,
    tag: `appointment_golden`,
    createdBy: `Sara Chen`,
    createdAt: `2026-08-21T09:00:00Z`,
  },
]

/** Items belonging to a dataset = items whose tags include the dataset's tag. */
export function itemsForDataset(dataset: Dataset, items: DatasetItem[]): DatasetItem[] {
  return items.filter((item) => item.tags.includes(dataset.tag))
}

/** Formats an ISO timestamp as a compact date, e.g. `Aug 14, 2026`. */
export function formatDate(iso: string): string {
  return new Intl.DateTimeFormat(`en-US`, { month: `short`, day: `numeric`, year: `numeric` }).format(new Date(iso))
}

/** Patient-type options for the golden editor (mirrors the extraction schema). */
export const PATIENT_TYPES = [`New Patient`, `Current Patient`, `Non-Patient`, `Other`]

/** Call-outcome options for the golden editor (from the callOutcomes enum). */
export const CALL_OUTCOMES = [
  `Appointment Scheduled`,
  `Appointment Not Scheduled`,
  `Appointment Rescheduled`,
  `Appointment Confirmation`,
  `Canceled Appointment`,
  `Voicemail`,
  `Missed Call`,
  `Interoffice Request`,
  `Referral Request`,
  `Request for Records`,
  `Insurance: State / HMO`,
  `Insurance: Out of Network`,
  `Office Too Far`,
  `Service Not Offered`,
  `Prescription Question`,
  `Wrong Number`,
  `Callback`,
  `Emergency`,
  `New Patient`,
  `Other`,
]

export function emptyPerson(): GoldenPerson {
  return {
    name: ``,
    isCaller: false,
    patientType: `New Patient`,
    appointmentRequested: false,
    appointmentScheduled: false,
    outcomes: [],
  }
}

export function emptyGolden(): GoldenExtraction {
  return { people: [{ ...emptyPerson(), isCaller: true }] }
}
